import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminInvoice } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Filter } from "@/ui/Filter";
import { Chip, type ChipTone } from "@/ui/Chip";
import { Button } from "@/ui/Button";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { ReasonDialog } from "@/features/admin/ReasonDialog";
import { ExportButton } from "@/features/admin/TableActions";
import { useFilters } from "@/features/admin/useFilters";
import { absoluteTime, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/invoices")({
  component: InvoicesScreen,
});

type Act = "mark-paid" | "retry-document" | "resend";

const ACTS: Record<Act, { label: string; title: string; description: string; confirm: string; prompt: string }> = {
  "mark-paid": {
    label: "Mark paid",
    title: "Settle this invoice by hand",
    description:
      "Rarely the right button. Transfers are collected automatically — the collection pass asks the bookkeeping system's bank feed which documents were settled. This is for money that arrived outside that.",
    confirm: "Mark it paid",
    prompt: "How it was settled",
  },
  "retry-document": {
    label: "Draw again",
    title: "Draw this invoice again",
    description:
      "The collection pass gives up after twenty attempts. An invoice that failed for a reason since fixed stays in draft for ever, and the customer is never billed for a month they used.",
    confirm: "Draw it",
    prompt: "What was fixed",
  },
  resend: {
    label: "Resend",
    title: "Send this invoice again",
    description:
      "The invoice if it is inside its term, the reminder if it is past due. Nothing is marked reminded and no clock is restarted — this is a courtesy, not three more days.",
    confirm: "Send it",
    prompt: "Why again",
  },
};

const STATES = ["", "draft", "issued", "paid", "uncollectible", "void"] as const;

/**
 * Every invoice on the installation.
 *
 * The three actions here are the ones a person has to make. Everything else
 * about collection is automatic and should stay that way: a staff surface that
 * can draw an invoice by hand is a surface that can draw two.
 */
function InvoicesScreen() {
  const filters = useFilters();
  const state = filters.get("state");
  const setState = (value: string) => filters.set("state", value);
  const [acting, setActing] = useState<{ invoice: AdminInvoice; act: Act } | null>(null);

  const invoices = useQuery({
    queryKey: ["platform-invoices", state],
    queryFn: ({ signal }) =>
      api.get<{ invoices: AdminInvoice[] | null }>(
        "/admin/invoices",
        { state: state || undefined, limit: 300 },
        signal,
      ),
  });

  const list = invoices.data?.invoices ?? [];
  // An invoice that has failed to draw is a month the customer used and will
  // never be billed for. Nothing else on the platform says so.
  const stuck = list.filter((i) => i.state === "draft" && (i.attempts ?? 0) > 0);

  return (
    <div className="flex flex-col gap-4">
      {stuck.length > 0 && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            {stuck.length === 1
              ? "One invoice could not be drawn"
              : `${stuck.length} invoices could not be drawn`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            Each is a month a customer used and will not be billed for: {stuck[0]?.error}
          </p>
        </div>
      )}

      <Panel
        actions={
          <div className="flex flex-wrap items-center gap-1.5">
            <ExportButton
              name="drumlin-invoices"
              rows={list}
              columns={[
                { header: "number", value: (i) => i.number ?? "" },
                { header: "organisation", value: (i) => i.organisation_name ?? "" },
                { header: "billing_email", value: (i) => i.billing_email ?? "" },
                { header: "period", value: (i) => i.period },
                { header: "state", value: (i) => i.state },
                { header: "net", value: (i) => i.net ?? "" },
                { header: "tax", value: (i) => i.tax ?? "" },
                { header: "gross", value: (i) => i.gross },
                { header: "tax_treatment", value: (i) => i.tax_treatment ?? "" },
                { header: "issued", value: (i) => i.issued_at ?? "" },
                { header: "due", value: (i) => i.due_at ?? "" },
                { header: "paid", value: (i) => i.paid_at ?? "" },
                { header: "error", value: (i) => i.error ?? "" },
              ]}
            />
          </div>
        }
        toolbar={
          <>
            {STATES.map((s) => (
              <Filter
                key={s || "all"}
                label={s || "all"}
                active={state === s}
                onClick={() => setState(s)}
              />
            ))}
          </>
        }
        padded={false}
      >
        {invoices.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={6} columns={6} />
          </div>
        ) : invoices.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={invoices.error} onRetry={() => void invoices.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title="No invoices"
              description={state ? `Nothing is ${state}.` : "No month has been closed yet."}
            />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[20%]">Organisation</Th>
                  <Th width="w-[12%]">Period</Th>
                  <Th width="w-[12%]">Number</Th>
                  <Th width="w-[12%]">State</Th>
                  <Th width="w-[11%]" numeric>
                    Gross
                  </Th>
                  <Th width="w-[11%]" align="right">
                    Due
                  </Th>
                  <Th width="w-[22%]" align="right">
                    Actions
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((i) => (
                  <Tr key={i.id} className={rowTone(i)}>
                    <Td clip title={i.billing_email || i.organisation_name}>
                      {i.organisation_id ? (
                        <Link
                          to="/admin/$orgId"
                          params={{ orgId: i.organisation_id }}
                          className="hover:text-[var(--primary)]"
                        >
                          {i.organisation_name}
                        </Link>
                      ) : (
                        i.organisation_name
                      )}
                    </Td>
                    <Td clip>{i.period}</Td>
                    <Td clip className="mono text-[13px]" title={i.error || undefined}>
                      {i.number || "—"}
                    </Td>
                    <Td clip title={i.error || i.tax_treatment}>
                      <Chip tone={stateTone(i)}>{i.state}</Chip>
                      {i.paid_by_card && (
                        <span className="ml-1.5 text-[13px] text-[var(--text-muted)]">card</span>
                      )}
                    </Td>
                    <Td numeric>{i.gross}</Td>
                    <Td align="right" title={i.due_at ? absoluteTime(i.due_at) : undefined}>
                      {i.due_at ? shortTime(i.due_at) : "—"}
                    </Td>
                    <Td align="right">
                      <div className="flex items-center justify-end gap-1">
                        {i.state === "draft" && (
                          <Button
                            tone="secondary"
                            size="dense"
                            onClick={() => setActing({ invoice: i, act: "retry-document" })}
                          >
                            {ACTS["retry-document"].label}
                          </Button>
                        )}
                        {i.state === "issued" && (
                          <>
                            <Button
                              tone="secondary"
                              size="dense"
                              onClick={() => setActing({ invoice: i, act: "resend" })}
                            >
                              {ACTS.resend.label}
                            </Button>
                            <Button
                              tone="secondary"
                              size="dense"
                              onClick={() => setActing({ invoice: i, act: "mark-paid" })}
                            >
                              {ACTS["mark-paid"].label}
                            </Button>
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      {acting && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setActing(null)}
          title={`${ACTS[acting.act].title}: ${acting.invoice.number || acting.invoice.period}`}
          description={ACTS[acting.act].description}
          prompt={ACTS[acting.act].prompt}
          confirmLabel={ACTS[acting.act].confirm}
          action={(reason) =>
            api.post<void>(`/admin/invoices/${acting.invoice.id}/${acting.act}`, { reason })
          }
          onDone={async () => {
            setActing(null);
            await invoices.refetch();
          }}
        />
      )}
    </div>
  );
}

function stateTone(i: AdminInvoice): ChipTone | undefined {
  switch (i.state) {
    case "paid":
      return "up";
    case "issued":
      return i.suspended_at ? "down" : i.reminded_at ? "warn" : undefined;
    case "draft":
      return (i.attempts ?? 0) > 0 ? "down" : undefined;
    case "uncollectible":
      return "down";
    default:
      return undefined;
  }
}

function rowTone(i: AdminInvoice): string | undefined {
  if (i.state === "draft" && (i.attempts ?? 0) > 0) return "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]";
  if (i.suspended_at) return "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]";
  if (i.reminded_at && i.state === "issued") return "bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]";
  return undefined;
}
